/**
 * URL Context Synchronizer
 *
 * This component synchronizes URL params (tenant, realmId) to the global
 * urlContextStore. This allows non-React code (Redux thunks, API helpers)
 * to access URL-based context.
 *
 * Place this component inside your Router but near the root of your app.
 *
 * @example
 * ```tsx
 * <BrowserRouter>
 *   <UrlContextSynchronizer />
 *   <App />
 * </BrowserRouter>
 * ```
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { urlContextStore } from 'core/urlContextStore';

export const UrlContextSynchronizer: React.FC = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tenant = searchParams.get('tenant') || undefined;
    const realmId = searchParams.get('realmId') || undefined;

    urlContextStore.setContext({ tenant, realmId });

    // Cleanup on unmount (shouldn't happen normally, but good practice)
    return () => {
      urlContextStore.clear();
    };
  }, [searchParams]);

  // This component renders nothing
  return null;
};

export default UrlContextSynchronizer;
